import { useEffect, useRef, useState } from 'react';
import ColorPicker from 'react-best-gradient-color-picker';

import { rgbaToHex } from 'src/utils/color';

const COLOR_PICKER_WIDTH = 240;
const COLOR_PICKER_HEIGHT = 175;
const COLOR_PICKER_PRESETS = [
  'rgba(16,16,16,1)', // Black
  'rgba(128,128,128,1)', // Gray
  'rgba(192,192,192,1)', // Light Gray
  'rgba(235,235,235,1)', // White
  'rgba(16,16,128,1)', // Navy
  'rgba(16,16,235,1)', // Blue
  'rgba(16,235,235,1)', // Cyan
  'rgba(16,128,16,1)', // Dark Green
  'rgba(128,128,16,1)', // Olive
  'rgba(16,128,128,1)', // Teal
  'rgba(16,235,16,1)', // Green
  'rgba(128,16,16,1)', // Maroon
  'rgba(128,16,128,1)', // Purple
  'rgba(175,51,235,1)', // Bright Purple / Magenta-ish
  'rgba(235,16,235,1)', // Magenta
  'rgba(235,16,16,1)', // Red
  'rgba(235,103,46,1)', // Orange
  'rgba(235,235,16,1)', // Yellow
];

type ColorPickerCustomProps = {
  defaultValue?: string;
  onChange: (value: string) => void;
};

const ColorPickerCustom = ({ defaultValue = '', onChange }: ColorPickerCustomProps) => {
  // Normalize 'none' to transparent black globally for color state
  const normalizedDefault = defaultValue === 'none' ? 'rgba(0,0,0,0)' : defaultValue;
  const [color, setColor] = useState(normalizedDefault);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  // Handle input click to clear default "0" values within ColorPicker
  useEffect(() => {
    const handleInputClick = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement)) {
        return;
      }

      // Only handle inputs within the ColorPicker component
      if (
        target &&
        target.tagName === 'INPUT' &&
        target.value === '0' &&
        colorPickerRef.current?.contains(target)
      ) {
        target.value = '';
        target.focus();
      }
    };

    const handleInputBlur = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement)) {
        return;
      }

      // Only handle inputs within the ColorPicker component
      if (
        target &&
        target.tagName === 'INPUT' &&
        target.value === '' &&
        colorPickerRef.current?.contains(target)
      ) {
        target.value = '0';
      }
    };

    // Add event listeners to the document
    document.addEventListener('click', handleInputClick);
    document.addEventListener('blur', handleInputBlur, true); // Use capture phase

    return () => {
      document.removeEventListener('click', handleInputClick);
      document.removeEventListener('blur', handleInputBlur, true);
    };
  }, []);

  const handleColorChange = (newColor: string) => {
    setColor(newColor);

    const hexColor = rgbaToHex(newColor);
    onChange(hexColor);
  };

  return (
    <div ref={colorPickerRef}>
      <ColorPicker
        // Class definition in index.scss
        className="custom-color-picker"
        value={color}
        onChange={handleColorChange}
        width={COLOR_PICKER_WIDTH}
        height={COLOR_PICKER_HEIGHT}
        hideColorTypeBtns
        hideGradientControls
        hideAdvancedSliders
        hideColorGuide
        hideGradientType
        disableLightMode
        presets={COLOR_PICKER_PRESETS}
      />
    </div>
  );
};

export default ColorPickerCustom;
